(function () {
  "use strict";

  const FILTER_UPDATE_EVENT = "bf:filter:updated";
  const DEBOUNCE_MS = 300;

  // ─── Utilities ──────────────────────────────────────────────────────────────

  function debounce(fn, delay) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Replaces the innerHTML of an element by ID using the freshly fetched document.
   * Safe-fails if either element is missing.
   */
  function swapRegion(id, newDoc) {
    const current = document.getElementById(id);
    const next = newDoc.getElementById(id);
    if (current && next) current.innerHTML = next.innerHTML;
  }

  // ─── BFCollectionFilters ────────────────────────────────────────────────────

  class BFCollectionFilters {
    /** @type {HTMLFormElement} */
    #form;
    /** @type {HTMLSelectElement|null} */
    #sortDesktop;
    /** @type {string} */
    #sectionId;
    /** @type {Map<string, string>} */
    #cache = new Map();
    #isLoading = false;

    constructor() {
      this.#form = document.querySelector("form.aside");
      this.#sortDesktop = document.getElementById("sort-by");

      if (!this.#form) return;

      this.#sectionId = this.#form.dataset.sectionId;
      if (!this.#sectionId) return;

      this.#bindFormEvents();
      this.#bindUIEvents();
      this.#bindPopState();
    }

    // ─── Event binding ─────────────────────────────────────────────────────────

    #bindFormEvents() {
      const debouncedUpdate = debounce(
        (params) => this.#renderPage(params),
        DEBOUNCE_MS,
      );

      // Checkbox changes — event delegation so it works after DOM swaps
      this.#form.addEventListener("change", (e) => {
        const target = /** @type {HTMLElement} */ (e.target);
        if (target.type === "checkbox") {
          debouncedUpdate(this.#buildParams());
        }
        // Mobile sort select (inside form, gets swapped — use delegation)
        if (target.id === "sort-by-mobile") {
          this.#renderPage(this.#buildParams({ sortValue: target.value }));
        }
      });

      // Apply button
      this.#form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.#renderPage(this.#buildParams());
      });

      // "Remove all" / "Clear" links — delegation catches replaced elements too
      this.#form.addEventListener("click", (e) => {
        const link = /** @type {HTMLElement} */ (e.target).closest(
          "a.clear, a.clear-filter",
        );
        if (link) {
          e.preventDefault();
          this.#renderPage("");
        }
      });

      // Desktop sort (outside form — not replaced, safe to bind directly)
      if (this.#sortDesktop) {
        this.#sortDesktop.addEventListener("change", () => {
          this.#renderPage(
            this.#buildParams({ sortValue: this.#sortDesktop.value }),
          );
        });
      }
    }

    #bindUIEvents() {
      // Filter group expand/collapse — delegation on form so it survives DOM swaps
      this.#form.addEventListener("click", (e) => {
        const btn = /** @type {HTMLElement} */ (e.target).closest("button.btn");
        if (!btn) return;
        const filter = btn.closest(".filter");
        if (!filter) return;
        filter.classList.toggle("filter--expanded");
        filter
          .querySelector(".filter__body")
          ?.classList.toggle("filter__body--expanded");
      });

      // Mobile sidebar open
      document
        .querySelector(".filters-mobile")
        ?.addEventListener("click", () => {
          this.#form.classList.add("show");
        });

      // Mobile sidebar close — delegation so it works after potential DOM swap
      this.#form.addEventListener("click", (e) => {
        if (/** @type {HTMLElement} */ (e.target).closest(".close-button")) {
          this.#form.classList.remove("show");
        }
      });
    }

    #bindPopState() {
      window.addEventListener("popstate", (e) => {
        const params = e.state?.searchParams ?? window.location.search.slice(1);
        this.#renderPage(params, false);
      });
    }

    // ─── Params builder ────────────────────────────────────────────────────────

    /**
     * Builds URLSearchParams string from the filter form + optional sort override.
     * Always strips `page` so filter changes reset to page 1.
     *
     * @param {{ sortValue?: string }} [opts]
     */
    #buildParams(opts = {}) {
      const formData = new FormData(this.#form);
      const params = new URLSearchParams(formData);

      // Prefer explicit sortValue; fall back to desktop select; fall back to form value
      const sortValue = opts.sortValue ?? this.#sortDesktop?.value;
      if (sortValue) params.set("sort_by", sortValue);

      params.delete("page");
      return params.toString();
    }

    // ─── Core render ───────────────────────────────────────────────────────────

    /**
     * Fetches the section with the given filter params and updates the DOM.
     *
     * @param {string} searchParams - URL-encoded filter params (may be empty)
     * @param {boolean} [pushState=true]
     */
    async #renderPage(searchParams, pushState = true) {
      if (this.#isLoading) return;

      // Build the canonical page URL (no `page` param — always reset to 1)
      const pageUrl = new URL(window.location.pathname, window.location.origin);
      if (searchParams) {
        new URLSearchParams(searchParams).forEach((v, k) =>
          pageUrl.searchParams.set(k, v),
        );
      }

      // Build the Section Rendering API URL
      const fetchUrl = new URL(pageUrl.toString());
      fetchUrl.searchParams.set("section_id", this.#sectionId);
      const fetchKey = fetchUrl.toString();

      this.#setLoading(true);

      let html = this.#cache.get(fetchKey);
      if (!html) {
        try {
          const res = await fetch(fetchKey);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          html = await res.text();
          this.#cache.set(fetchKey, html);
        } catch (err) {
          console.error("[BFCollectionFilters] fetch failed:", err);
          this.#setLoading(false);
          return;
        }
      }

      const newDoc = new DOMParser().parseFromString(html, "text/html");
      this.#updateDOM(newDoc);

      if (pushState) {
        history.pushState(
          { searchParams },
          "",
          pageUrl.toString() || window.location.pathname,
        );
      }

      this.#setLoading(false);

      // Notify infinite scroll (and any other listener) that filters changed
      document.dispatchEvent(new CustomEvent(FILTER_UPDATE_EVENT));
    }

    // ─── DOM updates ───────────────────────────────────────────────────────────

    #updateDOM(newDoc) {
      // Sidebar: header (active filter count) and content (filter options)
      swapRegion("bf-aside-header", newDoc);
      swapRegion("bf-aside-content", newDoc);

      // Content header: product count text + mobile badge
      swapRegion("bf-product-count", newDoc);
      swapRegion("bf-filter-badge", newDoc);

      // Products area: grid, sentinel, meta, pagination
      swapRegion("bf-products-area", newDoc);
    }

    // ─── Loading state ─────────────────────────────────────────────────────────

    #setLoading(active) {
      this.#isLoading = active;
      const area = document.getElementById("bf-products-area");
      if (area) area.classList.toggle("bf-loading", active);
    }
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => new BFCollectionFilters(),
    );
  } else {
    new BFCollectionFilters();
  }
})();
